
import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { ToastProvider } from "@/context/ToastContext";
import SolanaWalletProvider from "./providers/SolanaWalletProvider";
import { queryClient } from "./lib/query-client";

// Create a simple fallback for error states
const ErrorFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-swadez-bg-dark text-white p-6">
    <div className="text-center">
      <h2 className="text-2xl font-bold mb-4">Something went wrong</h2>
      <p className="mb-6">Please try refreshing the page</p>
      <button 
        onClick={() => window.location.reload()}
        className="bg-swadez-gold text-swadez-black px-4 py-2 rounded"
      >
        Refresh
      </button>
    </div>
  </div>
);

// Simple error boundary with proper typing
interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }
  
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  
  componentDidCatch(error: Error) {
    console.error("Application error:", error);
  }
  
  render() {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }
    return this.props.children;
  }
}

// Improved page loader with priority
const PageLoader = ({ children }: { children: React.ReactNode }) => (
  <React.Suspense fallback={
    <div className="min-h-screen flex items-center justify-center bg-swadez-bg-dark">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-swadez-gold"></div>
    </div>
  }>
    {children}
  </React.Suspense>
);

// Fix lazy loading pattern to ensure proper typing
const Index = React.lazy(() => import("./pages/Index"));
const Landing = React.lazy(() => import("./pages/Landing"));
const Auth = React.lazy(() => import("./pages/Auth"));
const CouponRedeem = React.lazy(() => import("./pages/CouponRedeem"));
const Welcome = React.lazy(() => import("./pages/Welcome"));
const WelcomeConfirmation = React.lazy(() => import("./pages/WelcomeConfirmation"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Staking = React.lazy(() => import("./pages/Staking"));
const ArenaSeats = React.lazy(() => import("./pages/ArenaSeats"));
const RealEstate = React.lazy(() => import("./pages/RealEstate"));
const NILFusion = React.lazy(() => import("./pages/NILFusion"));
const Music = React.lazy(() => import("./pages/Music"));
const TVAds = React.lazy(() => import("./pages/TVAds"));
const Esports = React.lazy(() => import("./pages/Esports"));
const Governance = React.lazy(() => import("./pages/Governance"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const Learn = React.lazy(() => import("./pages/Learn"));
const Partner = React.lazy(() => import("./pages/Partner"));
const Blog = React.lazy(() => import("./pages/Blog"));
const Swap = React.lazy(() => import("./pages/Swap"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const UserProfile = React.lazy(() => import("./pages/UserProfile"));
const Ecosystem = React.lazy(() => import("./pages/Ecosystem"));
const NFTMarketplace = React.lazy(() => import("./pages/NFTMarketplace"));
const ProtectedRoute = React.lazy(() => import("./components/auth/ProtectedRoute"));
const WhitePaper = React.lazy(() => import("./pages/WhitePaper"));
const Tokenomics = React.lazy(() => import("./pages/Tokenomics"));
const About = React.lazy(() => import("./pages/About"));
const TermsOfService = React.lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const FAQ = React.lazy(() => import("./pages/FAQ"));
const CookiePolicy = React.lazy(() => import("./pages/CookiePolicy"));
const OnePass = React.lazy(() => import("./pages/OnePass"));

// NFKEY pages
const NFKeyPage = React.lazy(() => import("./pages/nfkey/NFKeyPage"));
const SeatKey = React.lazy(() => import("./pages/nfkey/SeatKey"));
const AthloKey = React.lazy(() => import("./pages/nfkey/AthloKey"));
const AssetKey = React.lazy(() => import("./pages/nfkey/AssetKey"));
const RoyaltyKey = React.lazy(() => import("./pages/nfkey/RoyaltyKey"));
const SwadeKey = React.lazy(() => import("./pages/nfkey/SwadeKey"));
const SwadeSport = React.lazy(() => import("./pages/nfkey/SwadeSport"));
const AdKey = React.lazy(() => import("./pages/nfkey/AdKey"));

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            <TooltipProvider>
              <SolanaWalletProvider>
                <Toaster />
                <Routes>
                  <Route path="/" element={<PageLoader><Landing /></PageLoader>} />
                  <Route path="/home" element={<PageLoader><Index /></PageLoader>} />
                  <Route path="/auth" element={<PageLoader><Auth /></PageLoader>} />
                  <Route path="/welcome" element={<PageLoader><Welcome /></PageLoader>} />
                  <Route path="/welcome-confirmation" element={<PageLoader><WelcomeConfirmation /></PageLoader>} />
                  <Route path="/redeem" element={<PageLoader><CouponRedeem /></PageLoader>} />
                  <Route path="/dashboard" element={<PageLoader><ProtectedRoute><Dashboard /></ProtectedRoute></PageLoader>} />
                  <Route path="/staking" element={<PageLoader><Staking /></PageLoader>} />
                  <Route path="/arena-nfts" element={<PageLoader><ArenaSeats /></PageLoader>} />
                  <Route path="/real-estate" element={<PageLoader><RealEstate /></PageLoader>} />
                  <Route path="/nil-fusion" element={<PageLoader><NILFusion /></PageLoader>} />
                  <Route path="/music" element={<PageLoader><Music /></PageLoader>} />
                  <Route path="/tv-ads" element={<PageLoader><TVAds /></PageLoader>} />
                  <Route path="/esports" element={<PageLoader><Esports /></PageLoader>} />
                  <Route path="/governance" element={<PageLoader><ProtectedRoute><Governance /></ProtectedRoute></PageLoader>} />
                  <Route path="/learn" element={<PageLoader><Learn /></PageLoader>} />
                  <Route path="/whitepaper" element={<PageLoader><WhitePaper /></PageLoader>} />
                  <Route path="/partner" element={<PageLoader><Partner /></PageLoader>} />
                  <Route path="/blog" element={<PageLoader><Blog /></PageLoader>} />
                  <Route path="/swap" element={<PageLoader><Swap /></PageLoader>} />
                  <Route path="/ecosystem" element={<PageLoader><Ecosystem /></PageLoader>} />
                  <Route path="/nft-marketplace" element={<PageLoader><NFTMarketplace /></PageLoader>} />
                  <Route path="/tokenomics" element={<PageLoader><Tokenomics /></PageLoader>} />
                  <Route path="/about" element={<PageLoader><About /></PageLoader>} />
                  <Route path="/terms" element={<PageLoader><TermsOfService /></PageLoader>} />
                  <Route path="/privacy" element={<PageLoader><PrivacyPolicy /></PageLoader>} />
                  <Route path="/faq" element={<PageLoader><FAQ /></PageLoader>} />
                  <Route path="/cookies" element={<PageLoader><CookiePolicy /></PageLoader>} />
                  <Route path="/onepass" element={<PageLoader><OnePass /></PageLoader>} />
                  <Route path="/admin" element={<PageLoader><ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute></PageLoader>} />
                  <Route path="/profile" element={<PageLoader><ProtectedRoute><UserProfile /></ProtectedRoute></PageLoader>} />
                  
                  {/* NFKEY Routes */}
                  <Route path="/nfkey/:keyname" element={<PageLoader><NFKeyPage /></PageLoader>} />
                  <Route path="/seatkey" element={<PageLoader><SeatKey /></PageLoader>} />
                  <Route path="/athlokey" element={<PageLoader><AthloKey /></PageLoader>} />
                  <Route path="/assetkey" element={<PageLoader><AssetKey /></PageLoader>} />
                  <Route path="/royaltykey" element={<PageLoader><RoyaltyKey /></PageLoader>} />
                  <Route path="/swadekey" element={<PageLoader><SwadeKey /></PageLoader>} />
                  <Route path="/swadesport" element={<PageLoader><SwadeSport /></PageLoader>} />
                  <Route path="/adkey" element={<PageLoader><AdKey /></PageLoader>} />
                  
                  <Route path="*" element={<PageLoader><NotFound /></PageLoader>} />
                </Routes>
              </SolanaWalletProvider>
            </TooltipProvider>
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
